import React, { useEffect, useMemo, useState, Suspense, lazy, useCallback, useRef } from 'react'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  Box,
  Button,
  Checkbox,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Flex,
  Spinner
} from '@chakra-ui/react'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { SearchLgIcon } from '../../assets/icons'
import { getCountryNameByCode } from '../countries/PhoneDial'
import { useTranslation } from 'react-i18next'

interface FilterModalProps {
  isOpen: boolean
  onClose: () => void
  options: { id: string; name: string }[]
  /** Option ids that should be visible but not selectable */
  disabledOptionIds?: string[]
  selectedOptions: string[]
  onApply: (selectedOptions: string[]) => void
  title: string
  /** Override the footer action label (defaults to common.actions.apply) */
  applyLabel?: string
  children: React.ReactNode
  page?: number
  maxPage?: number
  onPageChange?: (page: number) => void
  search?: string
  setSearch?: (s: string) => void
  /** Infinite scroll: load more when user scrolls near bottom */
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  /** Show spinner when the filter options query is loading (e.g. while searching) */
  isLoading?: boolean
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  options,
  disabledOptionIds,
  selectedOptions,
  onApply,
  title,
  applyLabel,
  children,
  page,
  maxPage,
  onPageChange,
  search,
  setSearch,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isLoading,
}) => {
  const { t } = useTranslation()
  const disabledSet = useMemo(() => new Set(disabledOptionIds ?? []), [disabledOptionIds])
  const [selected, setSelected] = useState<string[]>(selectedOptions.filter((id) => !disabledSet.has(id)))
  const [searchTerm, setSearchTerm] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Keep internal selection in sync with parent (important for reuse outside table filters)
    setSelected(selectedOptions.filter((id) => !disabledSet.has(id)))
  }, [selectedOptions, disabledSet, isOpen])

  // Chakra Popover doesn't block background scrolling by default.
  // Lock body scroll while open so wheel/touch scroll stays within the list.
  useEffect(() => {
    if (!isOpen) return

    const body = document.body
    const html = document.documentElement

    const countAttr = 'data-filtermodal-scroll-lock-count'
    const prevOverflowAttr = 'data-filtermodal-prev-overflow'
    const prevPaddingAttr = 'data-filtermodal-prev-padding-right'

    const count = Number(body.getAttribute(countAttr) ?? '0')
    if (count === 0) {
      body.setAttribute(prevOverflowAttr, body.style.overflow || '')
      body.setAttribute(prevPaddingAttr, body.style.paddingRight || '')

      const scrollbarWidth = window.innerWidth - html.clientWidth
      body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`
    }
    body.setAttribute(countAttr, String(count + 1))

    return () => {
      const nextCount = Number(body.getAttribute(countAttr) ?? '1') - 1
      if (nextCount <= 0) {
        const prevOverflow = body.getAttribute(prevOverflowAttr) ?? ''
        const prevPaddingRight = body.getAttribute(prevPaddingAttr) ?? ''

        body.style.overflow = prevOverflow
        body.style.paddingRight = prevPaddingRight

        body.removeAttribute(countAttr)
        body.removeAttribute(prevOverflowAttr)
        body.removeAttribute(prevPaddingAttr)
      } else {
        body.setAttribute(countAttr, String(nextCount))
      }
    }
  }, [isOpen])

  // Avoid relying on translated title strings like "Filter by Country".
  // Instead, infer country filters from the option ids (2-letter country codes).
  const isCountryFilter = React.useMemo(() => {
    if (!Array.isArray(options) || options.length === 0) return false
    const twoLetter = options.filter((o) => typeof o.id === 'string' && o.id.length === 2)
    if (twoLetter.length === 0) return false
    return twoLetter.some((o) => Boolean(getCountryNameByCode(o.id)))
  }, [options])

  const handleScroll = useCallback(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return
    const el = scrollRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    const threshold = 60
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      onLoadMore()
    }
  }, [onLoadMore, hasMore, isLoadingMore])

  const handleCheckboxChange = (optionId: string) => {
    if (disabledSet.has(optionId)) return
    setSelected((prevSelected) =>
      prevSelected.includes(optionId)
        ? prevSelected.filter((item) => item !== optionId)
        : [...prevSelected, optionId]
    )
  }

  const handleReset = () => {
    setSelected([])
    setSearchTerm('')
    if (typeof search === 'string' && setSearch) setSearch('')
    onApply([])
    onClose()
  }

  const handleApply = () => {
    onApply(selected)
    onClose()
  }

  const showSearch = isCountryFilter || (typeof search === 'string' && setSearch)

  const optionsToDisplay =
    isCountryFilter
      ? options.filter((option) =>
        getCountryNameByCode(option.id)?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.id.toLowerCase().includes(searchTerm.toLowerCase())
      )
      : (typeof search === 'string' && setSearch)
        ? options
        : options.filter((option) =>
          option.name.toLowerCase().includes(searchTerm.toLowerCase())
        )

  const LazyCircleFlag = ({ countryCode }: { countryCode: string }) => {
    const CircleFlag = lazy(() => import('react-circle-flags').then((module) => ({ default: module.CircleFlag })))
    return (
      <Suspense fallback={<Box w="20px" h="20px" borderRadius="full" bg="solid.50" />}>
        <CircleFlag countryCode={countryCode} style={{ width: '20px', height: '20px' }} />
      </Suspense>
    )
  }

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent width="260px" p={0}>
        <PopoverHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottomWidth={showSearch ? '0px' : '1px'}
          borderColor="solid.50"
          px={4}
          pt={4}
          pb={showSearch ? 1 : 3}
        >
          <Text textStyle="h-xs" color="solid.900">{title}</Text>
          <Button
            variant="icon"
            size="xs"
            color="error.100"
            fontWeight="600"
            fontSize="12px"
            onClick={handleReset}
          >
            {t('common.actions.reset')}
          </Button>
        </PopoverHeader>

        {showSearch && (
          <Box px={3} pb={2}>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none" h="full">
                <Icon width="16px" height="16px" fill="none" color="solid.300">
                  <SearchLgIcon />
                </Icon>
              </InputLeftElement>
              <Input
                variant="filterSearch"
                placeholder={t('common.labels.searchPlaceholder')}
                fontSize="13px"
                borderRadius="6px"
                bg="solid.25"
                h="34px"
                value={typeof search === 'string' && setSearch ? search : searchTerm}
                onChange={(e) => {
                  if (typeof search === 'string' && setSearch) setSearch(e.target.value)
                  else setSearchTerm(e.target.value)
                }}
              />
            </InputGroup>
          </Box>
        )}

        <PopoverBody p={0}>
          <Box
            ref={scrollRef}
            maxH="200px"
            overflowY="auto"
            px={2}
            py={1}
            sx={{ overscrollBehavior: 'contain' }}
            onScroll={onLoadMore ? handleScroll : undefined}
          >
            <VStack align="stretch" spacing={0}>
              {isLoading && (
                <Flex justify="center" align="center" py={8}>
                  <Spinner size="md" color="solid.400" />
                </Flex>
              )}
              {!isLoading && optionsToDisplay.length === 0 && !isLoadingMore && (
                <Flex justify="center" py={4}>
                  <Text textStyle="l-xs" color="solid.300">{t('common.labels.noResults')}</Text>
                </Flex>
              )}
              {!isLoading && optionsToDisplay.map((option) => (
                (() => {
                  const isDisabled = disabledSet.has(option.id)
                  return (
                <HStack
                  key={option.id}
                  justifyContent="space-between"
                  alignItems="center"
                  px={2}
                  py="7px"
                  borderRadius="6px"
                  cursor={isDisabled ? 'not-allowed' : 'pointer'}
                  opacity={isDisabled ? 0.6 : 1}
                  transition="background 0.15s ease"
                  _hover={{ bg: 'solid.25' }}
                  onClick={() => handleCheckboxChange(option.id)}
                >
                  <HStack spacing={2} alignItems="center" flex={1} minW={0}>
                    {isCountryFilter && (
                      <LazyCircleFlag countryCode={option.id.toLowerCase()} />
                    )}
                    <Text textStyle="l-xs" color="solid.700" isTruncated>
                      {isCountryFilter ? (getCountryNameByCode(option.id) || option.id) : option.name}
                    </Text>
                  </HStack>
                  <Checkbox
                    isChecked={selected.includes(option.id)}
                    isDisabled={isDisabled}
                    onChange={() => handleCheckboxChange(option.id)}
                    onClick={(e) => e.stopPropagation()}
                    size="md"
                  />
                </HStack>
                  )
                })()
              ))}
              {!isLoading && onLoadMore && hasMore && isLoadingMore && (
                <Flex justify="center" py={2}>
                  <Spinner size="sm" color="solid.400" />
                </Flex>
              )}
            </VStack>
          </Box>
        </PopoverBody>

        <PopoverFooter borderColor="solid.50" px={3} py={3}>
          {!onLoadMore && typeof page === 'number' && typeof maxPage === 'number' && onPageChange && (
            <Flex justifyContent="space-between" alignItems="center" mb={2}>
              <Text textStyle="l-xxs" color="solid.400">
                {t('pagination.pageOf', { page, max: maxPage })}
              </Text>
              <HStack spacing={1}>
                <Button
                  size="xs"
                  variant="icon"
                  onClick={() => onPageChange(Math.max(page - 1, 1))}
                  isDisabled={page <= 1}
                  h="24px"
                  minW="24px"
                >
                  <ChevronLeftIcon boxSize={4} color="solid.400" />
                </Button>
                <Button
                  size="xs"
                  variant="icon"
                  onClick={() => onPageChange(page + 1)}
                  isDisabled={page >= maxPage}
                  h="24px"
                  minW="24px"
                >
                  <ChevronRightIcon boxSize={4} color="solid.400" />
                </Button>
              </HStack>
            </Flex>
          )}
          <Button variant="mono" width="100%" h="34px" fontSize="13px" borderRadius="8px" onClick={handleApply}>
            {applyLabel ?? t('common.actions.apply')}
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  )
}


export default FilterModal
